/**
 * Estado en memoria del vault zero-knowledge.
 *
 * Sostiene la cuenta desbloqueada (`accountKey`) y la clave del vault personal
 * SOLO en memoria (nunca se serializan). Deriva el estado de la máquina de
 * bloqueo combinando:
 *   - si existe una fila `vault_account` local (→ setup ya hecho), y
 *   - si tenemos las claves en memoria (→ desbloqueado).
 *
 * Auto-bloqueo por inactividad + bloqueo manual limpian las claves.
 */

import {
  type Accessor,
  createContext,
  createEffect,
  createMemo,
  createSignal,
  onCleanup,
  type ParentComponent,
  useContext,
} from "solid-js";
import {
  and,
  desc,
  eq,
  isNull,
  type PowerSyncDrizzleDB,
  vault_account as vaultAccountTable,
  type VaultItemRecord,
  vault as vaultTable,
} from "@boveda/powersync";
import {
  type AttachmentMeta,
  createPersonalVaultMutation,
  crypto,
  deleteVaultAttachmentMutation,
  deleteVaultItemMutation,
  generateUUID,
  getMyVaultMembershipQuery,
  insertVaultAttachmentMutation,
  nowISO,
  openMembershipRow,
  type ProtectedAccount,
  type ProtectedAttachment,
  queryKeys,
  restoreVaultItemMutation,
  type UnlockedAccount,
  upsertVaultAccount,
  upsertVaultItemMutation,
  type VaultItemPlain,
  type VaultMembershipRow,
} from "@boveda/shared";
import type { VaultAttachmentRecord } from "@boveda/powersync";
import { createPowerSyncQuery } from "@boveda/shared/solid";
import { useDrizzle } from "@/hooks/use-drizzle";
import { usePowerSyncContext } from "@/providers/power-sync-context";
import { useSession } from "@/lib/auth";

export type VaultStatus = "loading" | "needs-setup" | "locked" | "unlocked";

/** Item ya descifrado, listo para render (con id/rev para editar). */
export interface DecryptedVaultItem {
  readonly id: string;
  readonly rev: number;
  readonly plain: VaultItemPlain;
}

interface VaultAccountRow {
  id: string;
  user_id: string | null;
  kdf: string | null;
  protected_account_key: string | null;
  protected_private_key: string | null;
  public_key: string | null;
  auth_hash: string | null;
  recovery_blob: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface VaultRow {
  id: string;
  protected_key: string | null;
}

/** Clave de un vault de organización abierta desde MI membresía. */
export interface OpenedOrgVault {
  readonly key: Uint8Array;
  readonly epoch: number;
  readonly role: "owner" | "admin" | "member";
}

export interface VaultContextValue {
  status: Accessor<VaultStatus>;
  userId: Accessor<string>;
  vaultId: Accessor<string | null>;
  /**
   * Cuenta desbloqueada (claves SOLO en memoria). La necesitan los flujos de
   * org-vault/compartición (clave privada para abrir membresías/sobres). Nunca
   * serializar.
   */
  account: Accessor<UnlockedAccount | null>;
  /** Crea la cuenta zero-knowledge + vault personal y desbloquea. */
  setup: (password: string) => Promise<void>;
  /** Desbloquea con la contraseña maestra. Lanza Error si es incorrecta. */
  unlock: (password: string) => Promise<void>;
  /** Bloqueo manual: limpia las claves de memoria. */
  lock: () => void;
  /** Descifra una fila de item en memoria. */
  decryptRow: (row: VaultItemRecord) => Promise<DecryptedVaultItem>;
  /** Cifra y persiste un item (crea si no hay id; incrementa rev al editar). */
  saveItem: (plain: VaultItemPlain, existing?: { id: string; rev: number }) => Promise<string>;
  /**
   * Abre la clave de un vault de ORGANIZACIÓN desde mi membresía sincronizada
   * (sellada a mi clave pública). Cachea por época: una rotación (época nueva)
   * fuerza re-apertura. Lanza si no hay membresía (revocado / aún sin sync).
   */
  openOrgVault: (vaultId: string) => Promise<OpenedOrgVault>;
  /** Descifra una fila de item con una clave de vault explícita (org). */
  decryptRowWith: (key: Uint8Array, row: VaultItemRecord) => Promise<DecryptedVaultItem>;
  /** Cifra y persiste un item en un vault concreto con su clave (org). */
  saveItemWith: (
    targetVaultId: string,
    key: Uint8Array,
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ) => Promise<string>;
  deleteItem: (id: string) => Promise<void>;
  restoreItem: (id: string) => Promise<void>;
  /**
   * Adjunta un fichero cifrado a un item del vault PERSONAL. Cifra los bytes en
   * el cliente (clave de contenido por-adjunto) y persiste solo ciphertext.
   */
  attachFile: (
    itemId: string,
    filename: string,
    mime: string,
    bytes: Uint8Array,
  ) => Promise<string>;
  /** Descarga+descifra un adjunto del vault PERSONAL (bytes + metadatos en claro). */
  openAttachment: (row: VaultAttachmentRecord) => Promise<crypto.DecryptedAttachment>;
  deleteAttachment: (id: string) => Promise<void>;
  /** Reinicia el temporizador de auto-bloqueo (llamado en actividad del usuario). */
  keepAlive: () => void;
  /** Minutos de inactividad antes del auto-bloqueo. */
  autoLockMinutes: Accessor<number>;
  setAutoLockMinutes: (n: number) => void;
  drizzle: Accessor<PowerSyncDrizzleDB | null>;
}

const VaultContext = createContext<VaultContextValue>();

const DEFAULT_AUTO_LOCK_MINUTES = 5;
const MS_PER_MINUTE = 60_000;

export const VaultProvider: ParentComponent = (props) => {
  const drizzle = useDrizzle();
  const { db } = usePowerSyncContext();
  const session = useSession();
  const userId = () => session.userId();

  // Claves en memoria (nunca serializadas).
  const [unlocked, setUnlocked] = createSignal<UnlockedAccount | null>(null);
  const [vaultKey, setVaultKey] = createSignal<Uint8Array | null>(null);
  const [vaultId, setVaultId] = createSignal<string | null>(null);
  const [autoLockMinutes, setAutoLockMinutes] = createSignal(DEFAULT_AUTO_LOCK_MINUTES);
  // Claves de vaults de ORGANIZACIÓN abiertas (por vaultId, con su época).
  // Solo en memoria; se ceroizan en lock().
  const orgKeys = new Map<string, OpenedOrgVault>();

  // ¿Existe ya una cuenta local? (reactivo sobre PowerSync)
  const accountQuery = createPowerSyncQuery<VaultAccountRow>({
    queryKey: () => queryKeys.vault.account(userId()),
    queryFn: () => {
      const d = drizzle();
      return d
        ? d.select().from(vaultAccountTable).where(eq(vaultAccountTable.user_id, userId())).limit(1)
        : null;
    },
    db,
    enabled: () => Boolean(drizzle()) && Boolean(userId()),
  });

  const accountRow = createMemo<VaultAccountRow | null>(() => accountQuery.data?.[0] ?? null);

  const status = createMemo<VaultStatus>(() => {
    // Las claves EN MEMORIA de la cuenta ACTIVA mandan: una vez creada/desbloqueada
    // esta sesión, una recarga transitoria de `accountQuery` NO debe rebotar el
    // vault a "loading" y re-cerrar la UI. Esto pasaba justo tras un REGISTRO
    // nuevo: la escritura de `vault_account` re-dispara la query sobre la DB
    // per-usuario recién ligada (churn de bind, clase CC-29), dejándola en
    // `isLoading` mientras las claves ya estaban en memoria → el setup nunca
    // llegaba a "unlocked". Se exige `unlocked().userId === userId()` para que el
    // vault desbloqueado de una cuenta anterior JAMÁS se muestre bajo otra.
    const acc = unlocked();
    if (acc && acc.userId === userId() && vaultKey()) {
      return "unlocked";
    }
    if (!drizzle() || accountQuery.isLoading) {
      return "loading";
    }
    if (!accountRow()) {
      return "needs-setup";
    }
    return "locked";
  });

  // ── Auto-bloqueo por inactividad ─────────────────────────────────────────
  const [lastActivity, setLastActivity] = createSignal(Date.now());
  const keepAlive = () => setLastActivity(Date.now());

  createEffect(() => {
    if (status() !== "unlocked") {
      return;
    }
    const intervalMs = 15_000;
    const timer = setInterval(() => {
      const idleMs = Date.now() - lastActivity();
      if (idleMs >= autoLockMinutes() * MS_PER_MINUTE) {
        lock();
      }
    }, intervalMs);
    onCleanup(() => {
      clearInterval(timer);
    });
  });

  function lock(): void {
    // Cero las claves antes de descartarlas.
    const key = vaultKey();
    if (key) {
      key.fill(0);
    }
    const acc = unlocked();
    if (acc) {
      acc.accountKey.fill(0);
    }
    for (const opened of orgKeys.values()) {
      opened.key.fill(0);
    }
    orgKeys.clear();
    setVaultKey(null);
    setUnlocked(null);
  }

  onCleanup(() => {
    lock();
  });

  function rowToProtectedAccount(row: VaultAccountRow): ProtectedAccount {
    // El JSON del KDF lo escribió esta misma app (`upsertVaultAccount` serializa
    // un KdfConfig), así que la forma es de confianza; la aserción es inevitable
    // al hidratar desde SQLite (columna text).
    let parsedKdf: unknown = JSON.parse(row.kdf ?? "{}");
    if (typeof parsedKdf === "string") {
      // Fila que pasó por un servidor con el bug de doble codificación (JSONB
      // de un string JSON): el primer parse devuelve un string. Se re-parsea.
      parsedKdf = JSON.parse(parsedKdf);
    }
    return {
      userId: row.user_id ?? row.id,
      // oxlint-disable-next-line typescript-eslint/no-unsafe-type-assertion
      kdf: parsedKdf as ProtectedAccount["kdf"],
      protectedAccountKey: row.protected_account_key ?? "",
      protectedPrivateKey: row.protected_private_key ?? "",
      publicKey: row.public_key ?? "",
      authHash: row.auth_hash ?? "",
    };
  }

  async function loadPersonalVault(
    dbInstance: PowerSyncDrizzleDB,
    accountKey: Uint8Array,
  ): Promise<void> {
    const rows = (await dbInstance
      .select({ id: vaultTable.id, protected_key: vaultTable.protected_key })
      .from(vaultTable)
      .where(and(eq(vaultTable.owner_user_id, userId()), isNull(vaultTable.deleted_at)))
      .orderBy(desc(vaultTable.created_at))
      .limit(1)) as VaultRow[];
    const [row] = rows;
    if (!row?.protected_key) {
      throw new Error("vault not found");
    }
    const vk = await crypto.unwrapPersonalVaultKey(accountKey, row.protected_key);
    setVaultKey(vk);
    setVaultId(row.id);
  }

  async function setup(password: string): Promise<void> {
    const dbInstance = drizzle();
    if (!dbInstance) {
      throw new Error("database not ready");
    }
    const { protectedAccount, unlocked: unlockedAccount } = await crypto.createAccount(
      userId(),
      password,
    );
    await upsertVaultAccount(dbInstance, protectedAccount);

    const vk = crypto.createVaultKey();
    const wrapped = await crypto.wrapPersonalVaultKey(unlockedAccount.accountKey, vk);
    const id = generateUUID();
    await createPersonalVaultMutation(dbInstance, {
      id,
      ownerUserId: userId(),
      // MVP: el nombre "Personal" no es sensible → se guarda en claro. Los
      // nombres de vaults de organización SÍ irán cifrados en name_envelope.
      nameEnvelope: "Personal",
      protectedKey: wrapped,
    });

    setUnlocked(unlockedAccount);
    setVaultKey(vk);
    setVaultId(id);
    keepAlive();
  }

  async function unlock(password: string): Promise<void> {
    const dbInstance = drizzle();
    const row = accountRow();
    if (!dbInstance || !row) {
      throw new Error("no account to unlock");
    }
    // Lanza Error("invalid master password") si es incorrecta.
    const unlockedAccount = await crypto.unlockAccount(password, rowToProtectedAccount(row));
    await loadPersonalVault(dbInstance, unlockedAccount.accountKey);
    setUnlocked(unlockedAccount);
    keepAlive();
  }

  async function decryptRow(row: VaultItemRecord): Promise<DecryptedVaultItem> {
    const vk = vaultKey();
    if (!vk) {
      throw new Error("vault is locked");
    }
    const plain = await crypto.decryptItem(vk, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      envelope: row.envelope ?? "",
      rev: row.rev ?? 0,
    });
    return { id: row.id, rev: row.rev ?? 0, plain };
  }

  async function saveItem(
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ): Promise<string> {
    const dbInstance = drizzle();
    const vk = vaultKey();
    const vid = vaultId();
    if (!dbInstance || !vk || !vid) {
      throw new Error("vault is locked");
    }
    const id = existing?.id ?? generateUUID();
    const rev = existing ? existing.rev + 1 : 1;
    const withStamp: VaultItemPlain = { ...plain, updatedAt: nowISO() };
    const protectedItem = await crypto.encryptItem(vk, vid, id, rev, withStamp);
    await upsertVaultItemMutation(dbInstance, protectedItem);
    return id;
  }

  async function openOrgVault(orgVaultId: string): Promise<OpenedOrgVault> {
    const dbInstance = drizzle();
    const acc = unlocked();
    if (!dbInstance || !acc) {
      throw new Error("vault is locked");
    }
    const rows = (await getMyVaultMembershipQuery(
      dbInstance,
      orgVaultId,
      userId(),
    )) as VaultMembershipRow[];
    const [row] = rows;
    if (!row?.sealed_vault_key) {
      // Revocado, o la membresía aún no llegó por sync.
      throw new Error("no membership for this vault");
    }
    const epoch = row.key_epoch ?? 1;
    const cached = orgKeys.get(orgVaultId);
    if (cached && cached.epoch === epoch) {
      return cached;
    }
    const key = await openMembershipRow(acc.privateKey, row);
    const role = row.role === "owner" || row.role === "admin" ? row.role : "member";
    const opened: OpenedOrgVault = { key, epoch, role };
    // Una época nueva sustituye a la vieja (ceroizamos la anterior).
    if (cached) {
      cached.key.fill(0);
    }
    orgKeys.set(orgVaultId, opened);
    return opened;
  }

  async function decryptRowWith(
    key: Uint8Array,
    row: VaultItemRecord,
  ): Promise<DecryptedVaultItem> {
    const plain = await crypto.decryptItem(key, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      envelope: row.envelope ?? "",
      rev: row.rev ?? 0,
    });
    return { id: row.id, rev: row.rev ?? 0, plain };
  }

  async function saveItemWith(
    targetVaultId: string,
    key: Uint8Array,
    plain: VaultItemPlain,
    existing?: { id: string; rev: number },
  ): Promise<string> {
    const dbInstance = drizzle();
    if (!dbInstance) {
      throw new Error("database not ready");
    }
    const id = existing?.id ?? generateUUID();
    const rev = existing ? existing.rev + 1 : 1;
    const withStamp: VaultItemPlain = { ...plain, updatedAt: nowISO() };
    const protectedItem = await crypto.encryptItem(key, targetVaultId, id, rev, withStamp);
    await upsertVaultItemMutation(dbInstance, protectedItem);
    return id;
  }

  async function deleteItem(id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await deleteVaultItemMutation(dbInstance, id);
    }
  }

  async function restoreItem(id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await restoreVaultItemMutation(dbInstance, id);
    }
  }

  async function attachFile(
    itemId: string,
    filename: string,
    mime: string,
    bytes: Uint8Array,
  ): Promise<string> {
    const dbInstance = drizzle();
    const vk = vaultKey();
    const vid = vaultId();
    if (!dbInstance || !vk || !vid) {
      throw new Error("vault is locked");
    }
    const attachmentId = generateUUID();
    const meta: AttachmentMeta = { filename, mime, createdAt: nowISO() };
    const protectedAtt: ProtectedAttachment = await crypto.encryptAttachment({
      vaultKey: vk,
      vaultId: vid,
      itemId,
      attachmentId,
      bytes,
      meta,
    });
    await insertVaultAttachmentMutation(dbInstance, protectedAtt);
    return attachmentId;
  }

  async function openAttachment(row: VaultAttachmentRecord): Promise<crypto.DecryptedAttachment> {
    const vk = vaultKey();
    if (!vk) {
      throw new Error("vault is locked");
    }
    return crypto.decryptAttachment(vk, {
      id: row.id,
      vaultId: row.vault_id ?? "",
      itemId: row.item_id ?? "",
      wrappedKey: row.wrapped_key ?? "",
      metaEnvelope: row.meta_envelope ?? "",
      blob: row.blob ?? "",
      size: row.size ?? 0,
    });
  }

  async function deleteAttachment(id: string): Promise<void> {
    const dbInstance = drizzle();
    if (dbInstance) {
      await deleteVaultAttachmentMutation(dbInstance, id);
    }
  }

  const value: VaultContextValue = {
    status,
    userId,
    vaultId,
    account: unlocked,
    setup,
    unlock,
    lock,
    decryptRow,
    saveItem,
    openOrgVault,
    decryptRowWith,
    saveItemWith,
    deleteItem,
    restoreItem,
    attachFile,
    openAttachment,
    deleteAttachment,
    keepAlive,
    autoLockMinutes,
    setAutoLockMinutes,
    drizzle,
  };

  return <VaultContext.Provider value={value}>{props.children}</VaultContext.Provider>;
};

export function useVault(): VaultContextValue {
  const ctx = useContext(VaultContext);
  if (!ctx) {
    throw new Error("useVault must be used within <VaultProvider>");
  }
  return ctx;
}
